import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PhoneIcon from '@mui/icons-material/Phone';
import CampaignIcon from '@mui/icons-material/Campaign';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  contact: FunnelContact;

  handleOpenContactDetailsDialog(contact: FunnelContact): () => void;
}

const ContactCard: React.FC<Props> = ({ contact, handleOpenContactDetailsDialog }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Card key={contact.linkedInURL} className={classes.contactCard} elevation={0}>
      <CardContent className={classes.cardContent}>
        <Stack direction="row" gap={2} className={classes.cardHead}>
          <Stack>
            <Stack direction="row" alignItems="center" gap={1} mb="4px">
              <Typography
                className={classes.titleText}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenContactDetailsDialog(contact)();
                }}
                sx={{ cursor: 'pointer' }}
              >
                {contact.firstName} {contact.lastName}
              </Typography>
              {contact.aiJudgement && (
                <div
                  className={`${classes.judgementBadge} ${contact.aiJudgement === 'A'
                    ? classes.judgementBadgeA
                    : contact.aiJudgement === 'B'
                      ? classes.judgementBadgeB
                      : classes.judgementBadgeC
                    }`}
                >
                  {contact.aiJudgement}
                </div>
              )}
              {(contact as any).originFunnelName && (
                <Stack direction="row" alignItems="center" gap={0.5} className={classes.originChip}>
                  <CampaignIcon fontSize="inherit" className={classes.originIcon} />
                  <Typography className={classes.originText}>{(contact as any).originFunnelName}</Typography>
                </Stack>
              )}
            </Stack>
            {contact.email && (
              <Stack direction="row" alignItems="center" gap={1}>
                <AlternateEmailIcon className={classes.icon} />
                <Typography className={classes.greyText}>{contact.email}</Typography>
              </Stack>
            )}
            {contact.phoneNumber && (
              <Stack direction="row" alignItems="center" gap={1}>
                <PhoneIcon className={classes.icon} />
                <Typography className={classes.greyText}>{contact.phoneNumber}</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" className={classes.cardInfo}>
          <div className={classes.cardInfoContent}>
            <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
              <LinkedInIcon className={classes.icon} />
              {contact.linkedInURL ? (
                <PaiLink
                  text={_.truncate(contact.linkedInURL?.replace('talent/profile', 'in'), { length: 30 })}
                  underline="always"
                  href={contact.linkedInURL?.replace('talent/profile', 'in')}
                  target="_blank"
                  size="md"
                />
              ) : (
                <Typography className={classes.greyText}>{t('noLinkedInURL')}</Typography>
              )}
            </Stack>
            <Typography className={classes.greyText} mt="auto">
              {t('linkedInProfile')}
            </Typography>
          </div>
        </Stack>
        <Stack direction="row" className={classes.cardInfo}>
          <div className={classes.cardInfoContent}>
            <Typography className={classes.boldText}>{contact.occupation || '-'}</Typography>
            <Typography className={classes.greyText} mt="auto">
              {t('jobTitle')}
            </Typography>
          </div>
        </Stack>
        {contact.currentCompanies && contact.currentCompanies.length > 0 && (
          <Stack direction="row" className={classes.cardInfo}>
            <div className={classes.cardInfoContent}>
              <Stack spacing={0.5}>
                {Array.isArray(contact.currentCompanies) ? (
                  contact.currentCompanies.map((company, index) => (
                    <Typography key={index} className={classes.boldText}>
                      {typeof company === 'string' ? company : company.companyName}
                    </Typography>
                  ))
                ) : (
                  <Typography className={classes.boldText}>{contact.currentCompanies}</Typography>
                )}
              </Stack>
              <Typography className={classes.greyText} mt="auto">
                {t('company')}
              </Typography>
            </div>
          </Stack>
        )}
        {contact.location && (
          <Stack direction="row" className={classes.cardInfo}>
            <div className={classes.cardInfoContent}>
              <Typography className={classes.boldText}>{contact.location}</Typography>
              <Typography className={classes.greyText} mt="auto">
                {t('location')}
              </Typography>
            </div>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactCard: {
    transition: 'border-color 0.2s ease-in-out',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  cardContent: {
    padding: '0px !important',
  },
  cardHead: {
    padding: theme.spacing(1.5),
  },
  titleText: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  boldText: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  icon: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
  },
  cardInfo: {
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
  cardInfoContent: {
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  judgementBadge: {
    padding: theme.spacing(0.25, 0.75),
    borderRadius: '50%',
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  judgementBadgeA: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  judgementBadgeB: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  judgementBadgeC: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  originChip: {
    padding: theme.spacing(0.25, 0.75),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.dark,
    lineHeight: 1,
  },
  originIcon: {
    fontSize: theme.typography.pxToRem(12),
  },
  originText: {
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 600,
    textTransform: 'uppercase',
  },
}));

export default ContactCard;